import React, { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, X, FileImage, FileText, Type, Globe, Info } from 'lucide-react';
import { AdminTenantFormData } from './types';

interface Props {
  data: AdminTenantFormData;
  updateData: (updates: Partial<AdminTenantFormData>) => void;
}

const StepBrandAssets: React.FC<Props> = ({ data, updateData }) => {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const guidelinesInputRef = useRef<HTMLInputElement>(null);
  const fontsInputRef = useRef<HTMLInputElement>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      updateData({ logoFile: e.target.files[0] });
    }
  };

  const handleGuidelinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      updateData({ brandGuidelinesFile: e.target.files[0] });
    }
  };

  const handleFontsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      updateData({ fontFiles: [...data.fontFiles, ...Array.from(e.target.files)] });
    }
  };

  const removeFontFile = (index: number) => {
    const updated = data.fontFiles.filter((_, i) => i !== index);
    updateData({ fontFiles: updated });
  };

  return (
    <div className="space-y-6">
      {/* Use Website Design Checkbox */}
      <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
        <Checkbox
          id="use-website-design"
          checked={data.useWebsiteAsDesignReference}
          onCheckedChange={(checked) =>
            updateData({ useWebsiteAsDesignReference: checked === true })
          }
          className="mt-1"
        />
        <div>
          <label htmlFor="use-website-design" className="font-medium cursor-pointer flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Use website design as design reference
          </label>
          <p className="text-sm text-muted-foreground mt-1">
            We'll analyze the client's website to extract colors, fonts, and design style for their assistant. 
            You can still upload assets below to override specific elements.
          </p>
        </div>
      </div>

      {/* Info banner when checkbox is checked */}
      {data.useWebsiteAsDesignReference && (
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Website design will be used as the primary reference. File uploads below are optional overrides.
          </p>
        </div>
      )}

      {/* Logo Upload */}
      <div className="space-y-2">
        <Label>Logo {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(optional override)</span>}</Label>
        <input
          type="file"
          ref={logoInputRef}
          onChange={handleLogoChange}
          accept="image/*"
          className="hidden"
        />
        {data.logoFile ? (
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
            <FileImage className="h-5 w-5 text-muted-foreground" />
            <span className="flex-1 text-sm truncate">{data.logoFile.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => updateData({ logoFile: null })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => logoInputRef.current?.click()}
            className="w-full h-20 border-dashed"
          >
            <Upload className="h-5 w-5 mr-2" />
            Upload Logo
          </Button>
        )}
      </div>

      {/* Brand Guidelines Upload */}
      <div className="space-y-2">
        <Label>Brand Guidelines (PDF) {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(optional override)</span>}</Label>
        <input
          type="file"
          ref={guidelinesInputRef}
          onChange={handleGuidelinesChange}
          accept=".pdf"
          className="hidden"
        />
        {data.brandGuidelinesFile ? (
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <span className="flex-1 text-sm truncate">{data.brandGuidelinesFile.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => updateData({ brandGuidelinesFile: null })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => guidelinesInputRef.current?.click()}
            className="w-full h-16 border-dashed"
          >
            <Upload className="h-5 w-5 mr-2" />
            Upload Brand Guidelines
          </Button>
        )}
      </div>

      {/* Fonts Upload */}
      <div className="space-y-2">
        <Label>Fonts {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(optional override)</span>}</Label>
        <input
          type="file"
          ref={fontsInputRef}
          onChange={handleFontsChange}
          accept=".ttf,.otf,.woff,.woff2"
          multiple
          className="hidden"
        />
        {data.fontFiles.length > 0 && (
          <div className="space-y-2 mb-2">
            {data.fontFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-3 p-2 border rounded-lg bg-muted/30">
                <Type className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 text-sm truncate">{file.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFontFile(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => fontsInputRef.current?.click()}
          className="w-full border-dashed"
        >
          <Upload className="h-5 w-5 mr-2" />
          Add Font Files
        </Button>
      </div>

      {/* Brand Colors */}
      <div className="space-y-3">
        <Label>Brand Colors {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(can override website colors)</span>}</Label>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Primary</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={data.brandColors.primary}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, primary: e.target.value },
                  })
                }
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={data.brandColors.primary}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, primary: e.target.value },
                  })
                }
                placeholder="#0B1939"
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Secondary</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={data.brandColors.secondary}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, secondary: e.target.value },
                  })
                }
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={data.brandColors.secondary}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, secondary: e.target.value },
                  })
                }
                placeholder="#1a3a5c"
                className="flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Accent</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={data.brandColors.accent}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, accent: e.target.value },
                  })
                }
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                value={data.brandColors.accent}
                onChange={(e) =>
                  updateData({
                    brandColors: { ...data.brandColors, accent: e.target.value },
                  })
                }
                placeholder="#14b8a6"
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepBrandAssets;
